import moment from 'moment-timezone';

const VICTORIA_TIME_ZONE = 'Australia/Melbourne';
const HOLIDAY_CACHE = {};
const MONTH = {
  JANUARY: 0,
  MARCH: 2,
  APRIL: 3,
  JUNE: 5,
  NOVEMBER: 10,
  DECEMBER: 11,
};
const WEEKDAY = {
  SUNDAY: 0,
  MONDAY: 1,
  TUESDAY: 2,
  SATURDAY: 6,
};

const createVictorianDate = (year, month, day) =>
  moment.tz([year, month, day], VICTORIA_TIME_ZONE).startOf('day');

const formatHolidayKey = (date) =>
  date.clone().tz(VICTORIA_TIME_ZONE).format('YYYY-MM-DD');

const nthWeekdayOfMonth = (year, month, weekday, occurrence) => {
  const date = createVictorianDate(year, month, 1);

  while (date.day() !== weekday) {
    date.add(1, 'day');
  }

  return date.add(occurrence - 1, 'weeks');
};

const easterSunday = (year) => {
  const century = Math.floor(year / 100);
  const yearWithinCentury = year % 100;
  const correction = Math.floor(century / 4);
  const remainderCentury = century % 4;
  const f = Math.floor((century + 8) / 25);
  const g = Math.floor((century - f + 1) / 3);
  const h = (19 * (year % 19) + century - correction - g + 15) % 30;
  const i = Math.floor(yearWithinCentury / 4);
  const remainderYear = yearWithinCentury % 4;
  const l = (32 + 2 * remainderCentury + 2 * i - h - remainderYear) % 7;
  const m = Math.floor((year % 19 + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31) - 1;
  const day = ((h + l - 7 * m + 114) % 31) + 1;

  return createVictorianDate(year, month, day);
};

const addHoliday = (holidays, date, name) => {
  holidays[formatHolidayKey(date)] = {
    date: date.clone(),
    name,
  };
};

const isWeekend = (date) =>
  date.day() === WEEKDAY.SATURDAY || date.day() === WEEKDAY.SUNDAY;

const addObservedMondayHoliday = (holidays, date, name) => {
  if (date.day() === WEEKDAY.SATURDAY) {
    addHoliday(holidays, date.clone().add(2, 'days'), name);
  } else if (date.day() === WEEKDAY.SUNDAY) {
    addHoliday(holidays, date.clone().add(1, 'day'), name);
  }
};

const addNextAvailableWeekdayHoliday = (holidays, date, name) => {
  const observedDate = date.clone().add(1, 'day');

  while (
    isWeekend(observedDate) ||
    holidays[formatHolidayKey(observedDate)]
  ) {
    observedDate.add(1, 'day');
  }

  addHoliday(holidays, observedDate, name);
};

const addChristmasPeriodHolidays = (holidays, year) => {
  const christmasDay = createVictorianDate(year, MONTH.DECEMBER, 25);
  const boxingDay = createVictorianDate(year, MONTH.DECEMBER, 26);

  addHoliday(holidays, christmasDay, 'Christmas Day');
  addHoliday(holidays, boxingDay, 'Boxing Day');

  if (isWeekend(christmasDay)) {
    addNextAvailableWeekdayHoliday(holidays, christmasDay, 'Christmas Day');
  }

  if (isWeekend(boxingDay)) {
    addNextAvailableWeekdayHoliday(holidays, boxingDay, 'Boxing Day');
  }
};

const buildVictorianPublicHolidays = (year) => {
  if (HOLIDAY_CACHE[year]) {
    return HOLIDAY_CACHE[year];
  }

  const holidays = {};
  const newYearsDay = createVictorianDate(year, MONTH.JANUARY, 1);
  const australiaDay = createVictorianDate(year, MONTH.JANUARY, 26);
  const easterSundayDate = easterSunday(year);

  addHoliday(holidays, newYearsDay, "New Year's Day");
  addObservedMondayHoliday(holidays, newYearsDay, "New Year's Day");

  addHoliday(holidays, australiaDay, 'Australia Day');
  addObservedMondayHoliday(holidays, australiaDay, 'Australia Day');

  addHoliday(
    holidays,
    nthWeekdayOfMonth(year, MONTH.MARCH, WEEKDAY.MONDAY, 2),
    'Labour Day'
  );

  addHoliday(
    holidays,
    easterSundayDate.clone().subtract(2, 'days'),
    'Good Friday'
  );
  addHoliday(
    holidays,
    easterSundayDate.clone().subtract(1, 'day'),
    'Saturday before Easter Sunday'
  );
  addHoliday(holidays, easterSundayDate, 'Easter Sunday');
  addHoliday(
    holidays,
    easterSundayDate.clone().add(1, 'day'),
    'Easter Monday'
  );

  addHoliday(
    holidays,
    createVictorianDate(year, MONTH.APRIL, 25),
    'ANZAC Day'
  );
  addHoliday(
    holidays,
    nthWeekdayOfMonth(year, MONTH.JUNE, WEEKDAY.MONDAY, 2),
    "King's Birthday"
  );
  addHoliday(
    holidays,
    nthWeekdayOfMonth(year, MONTH.NOVEMBER, WEEKDAY.TUESDAY, 1),
    'Melbourne Cup'
  );

  addChristmasPeriodHolidays(holidays, year);

  HOLIDAY_CACHE[year] = holidays;
  return holidays;
};

const getHolidayForDate = (date) => {
  const holidays = buildVictorianPublicHolidays(date.year());
  return holidays[formatHolidayKey(date)] || null;
};

const isSupportBusinessDay = (date) => {
  const weekday = date.day();

  return (
    weekday !== WEEKDAY.SATURDAY &&
    weekday !== WEEKDAY.SUNDAY &&
    !getHolidayForDate(date)
  );
};

export const getVictorianPublicHolidayNotice = (
  currentMoment = moment().tz(VICTORIA_TIME_ZONE)
) => {
  const today = currentMoment.clone().tz(VICTORIA_TIME_ZONE).startOf('day');
  const holiday = getHolidayForDate(today);

  if (!holiday) {
    return null;
  }

  const resumeDate = today.clone().add(1, 'day');

  while (!isSupportBusinessDay(resumeDate)) {
    resumeDate.add(1, 'day');
  }

  return {
    holidayName: holiday.name,
    resumeDate,
    resumeDateText: resumeDate.format('dddd D MMMM YYYY'),
  };
};
